import { useRef } from 'react';
import { hasRowNum, TableRow } from './tableColumns';

const isPlainObject = (value: object) => {
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
};

/** Равенство значений строки формы: примитивы — `Object.is`, даты — по времени, массивы и простые объекты —
 *  поэлементно, прочие объекты — по ссылке. Строже `deepEqual` из helpers (тот равняет дату со строкой ISO и
 *  `null` с `undefined`): прежний объект строки показал бы прежнее значение. Не `react-fast-compare`: тот сравнивает
 *  экземпляры классов по полям и через `valueOf`, а снимок (`snapshotOf`) держит их по ссылке, — равенство и
 *  снимок должны видеть значение одинаково; ещё он равняет `0` с `-0`. */
export const sameRowValue = (a: unknown, b: unknown): boolean => {
    if (Object.is(a, b)) return true;
    if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
    if (a instanceof Date || b instanceof Date) {
        return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
    }
    if (Array.isArray(a) || Array.isArray(b)) {
        if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
        return a.every((item, index) => sameRowValue(item, b[index]));
    }
    if (!isPlainObject(a) || !isPlainObject(b)) return false;
    const keysA = Object.keys(a);
    if (keysA.length !== Object.keys(b).length) return false;
    return keysA.every(
        (key) =>
            Object.prototype.hasOwnProperty.call(b, key) &&
            sameRowValue((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]),
    );
};

/** Копия для снимка: массивы, простые объекты и даты — свои, прочее — по ссылке (как сравнивает `sameRowValue`). */
const snapshotOf = <V>(value: V): V => {
    if (typeof value !== 'object' || value === null) return value;
    if (value instanceof Date) return new Date(value.getTime()) as V;
    if (Array.isArray(value)) return value.map(snapshotOf) as V;
    if (!isPlainObject(value)) return value;
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, snapshotOf(item)])) as V;
};

const keyOf = (row: TableRow) => (hasRowNum(row) ? String(row['|NUM']) : undefined);

interface IStableRowsState {
    input: TableRow[];
    result: TableRow[];
    /** Содержимое отданных строк на момент выдачи. */
    snapshots: WeakMap<TableRow, TableRow>;
    /** Строки, отданные не из значений формы (клоны useWatch, свои копии): RHF их на месте не меняет. */
    detached: WeakSet<TableRow>;
}

/** Строки с прежними объектами там, где содержимое не изменилось.
 *
 *  useWatch клонирует значение на каждое событие формы: правка одной ячейки давала новые объекты всем строкам, и
 *  кэш строк DataTable (сравнение по ссылке на строку) перерисовывал таблицу целиком. Строка сопоставляется с
 *  прежней по `|NUM`, без ключа — по позиции; прежняя строка не отдаётся двум новым. Без изменений возвращается
 *  прежний массив.
 *
 *  Сравнение идёт со снимком, снятым при выдаче строки, а не с самим объектом: первый ответ useWatch — объекты из
 *  значений формы, и RHF меняет их на месте (`setValue` по пути ячейки), так же может поступить и прикладной код.
 *  Изменённый на месте объект совпал бы с новым значением, и грид не перерисовал бы строку; со снимком такая строка
 *  получает новый объект. `getLive` (`getValues` по пути таблицы) говорит, какие строки — значения формы: с ним
 *  прежний объект сверяется со снимком, только когда это ничем другим не покрыто (`unchangedInPlace`). */
export const useStableRows = (rows: TableRow[], getLive?: () => unknown): TableRow[] => {
    const stateRef = useRef<IStableRowsState | null>(null);
    const state = stateRef.current;
    // вход тем же массивом (рендер не от события формы) — прежний ответ без сравнения
    if (state?.input === rows) return state.result;

    const snapshots = state?.snapshots ?? new WeakMap<TableRow, TableRow>();
    const detached = state?.detached ?? new WeakSet<TableRow>();
    const live = getLive?.();
    const liveRows = getLive ? new Set<unknown>(Array.isArray(live) ? live : []) : undefined;
    // Со снимком сверяется только прежний объект, который был значением формы и перестал им быть (setValue всей
    // таблицы кладёт в форму клон): отданный не из значений формы на месте не меняется, а нынешнее значение формы
    // вход и повторяет. Сверенный однажды бывший объект формы дальше тоже не меняется.
    const unchangedInPlace = (candidate: TableRow, snapshot: TableRow) => {
        if (liveRows && (liveRows.has(candidate) || detached.has(candidate))) return true;
        if (!sameRowValue(snapshot, candidate)) return false;
        if (liveRows) detached.add(candidate);
        return true;
    };
    const prev = state?.result ?? [];
    const prevByKey = new Map<string, TableRow>();
    prev.forEach((row) => {
        const key = keyOf(row);
        if (key !== undefined && !prevByKey.has(key)) prevByKey.set(key, row);
    });
    const used = new Set<TableRow>();
    let unchanged = prev.length === rows.length;
    const next = rows.map((row, index) => {
        const key = keyOf(row);
        const candidate = key !== undefined ? prevByKey.get(key) : prev[index];
        const snapshot = candidate && snapshots.get(candidate);
        const reusable =
            !!candidate &&
            !!snapshot &&
            !used.has(candidate) &&
            (key !== undefined || keyOf(candidate) === undefined) &&
            sameRowValue(snapshot, row) &&
            unchangedInPlace(candidate, snapshot);
        let result = reusable ? candidate : row;
        if (!reusable) {
            const own = snapshots.get(row);
            // уже отданный объект пришёл изменённым на месте: перерисовать строку можно только новым объектом
            if (own && !sameRowValue(own, row)) result = { ...row };
            if (result !== row || !own) snapshots.set(result, snapshotOf(result));
            if (result !== row || (liveRows && !liveRows.has(row))) detached.add(result);
        }
        used.add(result);
        if (result !== prev[index]) unchanged = false;
        return result;
    });
    const result = unchanged ? prev : next;
    stateRef.current = { input: rows, result, snapshots, detached };
    return result;
};
